import { ExchangeData } from "../types/budget/budget";
import { ServiceValues } from "../types/budget/service-item";
import CoinData from "./Common-Currency.json";
import { getCurrencyDisplayLabel } from "./currencyDisplay";
import { sortCurrencyOptions } from "./currencySort";

/**
 * Obtém opções de moeda ordenadas (BRL, USD, EUR primeiro)
 */
export function getCoinOptions() {
  return sortCurrencyOptions(
    CoinData.map((el) => ({
      value: el.code,
      label: getCurrencyDisplayLabel(el.name),
    }))
  );
}

/**
 * Obtém o câmbio do evento a partir dos dados do projeto
 * Para eventos nacionais, retorna valores padrão BRL (sem conversão)
 */
export function getEventExchange(event: any): ExchangeData | null {
  // Para eventos nacionais, sempre retorna BRL sem conversão
  if (event?.eventScope === "Nacional") {
    return {
      date: "",
      currency: "BRL",
      value: 1,
    };
  }

  if (
    !event?.exchangeDate ||
    !event?.exchangeCurrency ||
    !event?.exchangeValue
  ) {
    return null;
  }

  return {
    date: event.exchangeDate,
    currency: event.exchangeCurrency,
    value: event.exchangeValue,
  };
}

/**
 * Verifica se o evento requer câmbio (Internacional ou Ambos)
 */
export function eventRequiresExchange(eventScope: string): boolean {
  return eventScope === "INTERNATIONAL" || eventScope === "BOTH";
}

/**
 * Aplica o câmbio do evento a um ServiceValues se não houver override
 * Para eventos nacionais, sempre usa BRL sem conversão
 */
export function applyEventExchangeToService(
  serviceValues: ServiceValues,
  eventExchange: ExchangeData | null
): ServiceValues {
  // Se já tem override manual, não altera
  if (serviceValues.exchangeOverride) {
    return serviceValues;
  }

  // Se não há câmbio do evento, mantém o padrão BRL
  // Isso também se aplica a eventos nacionais (eventExchange será { currency: "BRL", value: 1 })
  if (!eventExchange) {
    return {
      ...serviceValues,
      exchangeData: {
        date: "",
        currency: "BRL",
        value: 1,
      },
      exchangeOverride: false,
    };
  }

  // Aplica o câmbio do evento
  // Para eventos nacionais, eventExchange já virá com { currency: "BRL", value: 1 }
  return {
    ...serviceValues,
    exchangeData: { ...eventExchange },
    exchangeOverride: false,
  };
}

/**
 * Aplica o câmbio do evento às categorias (baseValues) de um orçamento.
 *
 * Categorias com câmbio personalizado (`isOverride`) são preservadas: elas só
 * voltam a seguir Dados Cadastrais pelo botão "Restaurar padrão" da própria
 * categoria. As demais passam a refletir o novo câmbio do evento.
 */
export function applyEventExchangeToBaseValues<
  T extends { exchange?: ExchangeData; isOverride?: boolean }
>(baseValues: T[] | undefined | null, eventExchange: ExchangeData): T[] {
  return (baseValues || []).map((baseValue) =>
    baseValue.isOverride
      ? baseValue
      : {
          ...baseValue,
          exchange: {
            date: eventExchange.date,
            currency: eventExchange.currency,
            value: eventExchange.value,
          },
          isOverride: false,
        }
  );
}

/**
 * Marca o câmbio de um serviço como override manual
 */
export function markExchangeAsOverride(
  serviceValues: ServiceValues
): ServiceValues {
  return {
    ...serviceValues,
    exchangeOverride: true,
  };
}

/**
 * Remove o override e reaplica o câmbio do evento
 */
export function resetExchangeOverride(
  serviceValues: ServiceValues,
  eventExchange: ExchangeData | null
): ServiceValues {
  return applyEventExchangeToService(
    { ...serviceValues, exchangeOverride: false },
    eventExchange
  );
}

/**
 * Câmbio efetivo do serviço nos cálculos.
 * O override individual (`exchangeOverride`) tem prioridade sobre o câmbio
 * já resolvido da categoria (evento ou personalizado da categoria).
 */
export function resolveEffectiveServiceExchange(
  serviceValues:
    | Pick<ServiceValues, "exchangeData" | "exchangeOverride">
    | undefined,
  categoryResolvedExchange: ExchangeData | undefined
): ExchangeData {
  if (serviceValues?.exchangeOverride && serviceValues.exchangeData) {
    return serviceValues.exchangeData;
  }

  return (
    categoryResolvedExchange ??
    serviceValues?.exchangeData ?? {
      date: "",
      currency: "BRL",
      value: 1,
    }
  );
}

/**
 * Verifica se dois câmbios são iguais
 */
export function areExchangesEqual(
  exchange1: ExchangeData,
  exchange2: ExchangeData
): boolean {
  return (
    exchange1.date === exchange2.date &&
    exchange1.currency === exchange2.currency &&
    exchange1.value === exchange2.value
  );
}

/**
 * Atualiza todos os serviços com o novo câmbio do evento,
 * mas apenas aqueles que não têm override manual
 */
export function updateServicesExchange(
  services: ServiceValues[],
  eventExchange: ExchangeData | null
): ServiceValues[] {
  return services.map((service) =>
    applyEventExchangeToService(service, eventExchange)
  );
}
